import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Users, Calendar, ArrowRight } from "lucide-react";
import ClientLayout from "../../components/Layout/ClientLayout";
import HeroComponentRedesign from "../../components/hero/HeroComponentRedesign";
import { useNavigate } from "react-router-dom";
import { BiWorld } from "react-icons/bi";
import { FaUserDoctor } from "react-icons/fa6";
import { FaHandsHelping, FaInstagram, FaExternalLinkAlt } from "react-icons/fa";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import {
  useGetAdventuresQuery,
  useGetProgrammesQuery,
} from "../../apiCaller/apis/getApi";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";

import Kilimanjaro1 from "../../assets/images/home_gallery/kilimanjaro1.png";
import Kilimanjaro2 from "../../assets/images/home_gallery/kilimanjaro2.jpg";
import Elephant1 from "../../assets/images/home_gallery/elephant1.jpg";
import Satima1 from "../../assets/images/home_gallery/satima1.jpg";
import Longido1 from "../../assets/images/home_gallery/longido1.jpg";

const HomeHybrid = () => {
  const navigate = useNavigate();
  const [adventureSearch] = useState({ totalItems: 4 });

  // Fetch data
  const { data: adventures, isLoading: adventuresIsLoading } = GetTableListAPI(
    adventureSearch,
    useGetAdventuresQuery,
  );

  const { data: programmes, isLoading: programmesIsLoading } = GetTableListAPI(
    {},
    useGetProgrammesQuery,
  );

  const handleWhatsapp = () => {
    const groupInviteLink = "https://chat.whatsapp.com/LiiuHHZpLQcBewqlGSEqF2";
    window.open(groupInviteLink, "_blank");
  };

  // Shop products data
  const shopProducts = [
    {
      id: 1,
      name: "Orok Hat",
      description:
        "Premium hiking hat for sun protection and style on the trails",
      instagramUrl:
        "https://www.instagram.com/p/DNkdK05t-ep/?igsh=MXUxbG8xNnIzdWRkMQ==",
      category: "Headwear",
      featured: true,
    },
    {
      id: 2,
      name: "Virunga Backpack",
      description: "Durable hiking backpack designed for multi-day adventures",
      instagramUrl:
        "https://www.instagram.com/p/DNOVgaDtzyC/?igsh=MW5haGMyYWoyYTBicg==",
      category: "Bags",
      featured: true,
    },
    {
      id: 3,
      name: "Kinangop Poles",
      description: "Lightweight trekking poles for stability and support",
      instagramUrl:
        "https://www.instagram.com/p/DOWJe8VjeB4/?igsh=MTMxbDFvOXFvajQ3Zg==",
      category: "Equipment",
      featured: false,
    },
    {
      id: 4,
      name: "Iberg Bladder",
      description:
        "High-capacity hydration bladder for long hiking expeditions",
      instagramUrl:
        "https://www.instagram.com/p/DOITwEPjRV3/?igsh=ZGhuZzhwNHVrdWth",
      category: "Hydration",
      featured: false,
    },
  ];

  const handleProductClick = (instagramUrl) => {
    window.open(instagramUrl, "_blank");
  };

  // Format date to "2nd Jan 2026" style
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const year = date.getFullYear();
    
    // Add ordinal suffix (st, nd, rd, th)
    const suffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${suffix(day)} ${month} ${year}`;
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  // Slideshow state for programs with timer
  const [currentProgramIndex, setCurrentProgramIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const allProgrammes = programmes?.results || [];
  const featuredProgramme = allProgrammes[currentProgramIndex];
  const slideDuration = 6000; // 6 seconds per slide

  // Auto-advance slideshow with progress bar
  useEffect(() => {
    if (allProgrammes.length > 1 && !isPaused) {
      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            setCurrentProgramIndex(
              (current) => (current + 1) % allProgrammes.length,
            );
            return 0;
          }
          return prev + 100 / (slideDuration / 50);
        });
      }, 50);
      return () => clearInterval(progressInterval);
    }
  }, [allProgrammes.length, isPaused, currentProgramIndex]);

  // Reset progress when manually changing slides
  const handleSlideChange = (index) => {
    setCurrentProgramIndex(index);
    setProgress(0);
  };

  return (
    <ClientLayout>
      {/* Redesigned Hero Section */}
      <div className="px-5">
        <HeroComponentRedesign />
      </div>

      {/* REDESIGNED Programs Section - Mamba Theme */}
      <section className="relative py-24 px-10 md:px-16 lg:px-48 bg-gradient-to-b from-white via-mamba-bg-light to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="w-full"
        >
          {/* Section Header */}
          <motion.div
            variants={fadeInUp}
            className="flex justify-between items-end mb-16"
          >
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px w-12 bg-gradient-to-r from-mamba-golden to-transparent" />
                <span className="text-xs font-montserrat font-bold uppercase tracking-[0.2em] text-mamba-navy">
                  Training Programs
                </span>
              </div>
              <h2 className="text-3xl lg:text-5xl xl:text-6xl font-montserrat font-black text-mamba-navy leading-tight tracking-tight">
                From Beginner
                <span className="block bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent">
                  To Summit Master
                </span>
              </h2>
              <p className="text-base text-slate-600 mt-4 font-inter">
                Progressive programs designed to elevate your{" "}
                <span className="font-dancing italic text-mamba-golden">
                  Mamba Hikers
                </span>{" "}
                journey
              </p>
            </div>
            <button
              onClick={() => navigate("/programmes")}
              className="hidden lg:flex items-center gap-2 text-mamba-navy font-montserrat font-semibold hover:gap-4 transition-all group"
            >
              View All Programs
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>

          {/* Programs Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programmesIsLoading ? (
              // Loading skeletons
              [...Array(3)].map((_, index) => (
                <div
                  key={`skeleton-${index}`}
                  className="bg-gradient-to-br from-white to-slate-50 rounded-3xl overflow-hidden border-2 border-mamba-sky-blue/20"
                >
                  <div className="relative h-72 bg-slate-200 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-6 bg-slate-200 rounded animate-pulse w-3/4" />
                    <div className="h-4 bg-slate-200 rounded animate-pulse w-full" />
                    <div className="h-4 bg-slate-200 rounded animate-pulse w-5/6" />
                  </div>
                </div>
              ))
            ) : programmes?.results && programmes.results.length > 0 ? (
              programmes.results.slice(0, 3).map((program, index) => (
                <motion.div
                  key={program.id}
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.2,
                    ease: "easeOut",
                  }}
                  whileHover={{ y: -12 }}
                  onClick={() => navigate(`/programmes/${program.slug}`)}
                  className="group relative bg-gradient-to-br from-white to-slate-50 rounded-3xl overflow-hidden border-2 border-mamba-sky-blue/20 hover:border-mamba-golden transition-all duration-500 hover:shadow-2xl cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative h-72 overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      src={
                        program.display_image_url ||
                        `https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=80`
                      }
                      alt={program.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=80`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent" />

                    {/* Floating stats on image */}
                    <div className="absolute bottom-4 left-4 right-4 flex gap-2">
                      {program.duration && (
                        <div className="px-3 py-2 bg-white/90 backdrop-blur-sm rounded-lg text-xs font-bold text-slate-900">
                          {program.duration} {program.duration_units}
                        </div>
                      )}
                      {program.total_adventures && (
                        <div className="px-3 py-2 bg-mamba-sky-blue/90 backdrop-blur-sm rounded-lg text-xs font-bold text-white">
                          {program.total_adventures} Adventures
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <h3 className="text-2xl font-montserrat font-black text-mamba-navy mb-3 leading-tight group-hover:text-mamba-sky-blue transition-colors">
                      {program.name}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4 line-clamp-3 font-inter">
                      {program.description}
                    </p>

                    {/* Stats row */}
                    <div className="flex items-center justify-between pt-4 border-t border-mamba-sky-blue/20">
                      {program.price && (
                        <div>
                          <div className="text-xs text-slate-500 mb-1">
                            From
                          </div>
                          <div className="text-xl font-montserrat font-black text-mamba-navy">
                            KSh {(program.price / 1000).toFixed(0)}K
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-mamba-sky-blue font-semibold text-sm group-hover:gap-3 transition-all">
                        Explore
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  {/* Accent gradient at bottom */}
                  <div className="h-1 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </motion.div>
              ))
            ) : (
              // No programmes available
              <div className="col-span-full text-center py-12">
                <p className="text-slate-600 text-lg font-inter">
                  No training programs available at the moment.
                </p>
              </div>
            )}
          </div>

          {/* Mobile View All Button */}
          <div className="mt-12 text-center lg:hidden">
            <button
              onClick={() => navigate("/programmes")}
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white font-montserrat font-bold text-sm uppercase tracking-wider rounded-full hover:shadow-2xl transition-all"
            >
              View All Programs
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </section>

      {/* CREATIVE BANNER 1: Image Gallery Mosaic */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
        <div className="w-full px-10 md:px-16 lg:px-48">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl lg:text-5xl font-montserrat font-black text-mamba-navy mb-4">
              Moments That
              <span className="block bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent">
                Take Your Breath Away
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-inter">
              Real adventures, real people, unforgettable experiences
            </p>
          </motion.div>

          {/* Image Mosaic Grid */}
          <div className="grid grid-cols-4 gap-4 h-[500px]">
            {/* Large left image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="col-span-4 lg:col-span-2 row-span-2 relative group overflow-hidden rounded-3xl"
            >
              <img
                src={Kilimanjaro1}
                // src="https://images.unsplash.com/photo-1483728642387-6c3bdd6c93e5?w=800&q=90"
                alt="Mountain adventure"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute bottom-6 left-6 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <p className="text-2xl font-bold">Summit Success</p>
                <p className="text-sm text-white/80">
                  Mount Kilimanjaro Expedition
                </p>
              </div>
            </motion.div>

            {/* Top right */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="col-span-2 lg:col-span-1 relative group overflow-hidden rounded-3xl"
            >
              <img
                src={Elephant1}
                // src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=90"
                alt="Hiking group"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/80 to-transparent" />
            </motion.div>

            {/* Middle right */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="col-span-2 lg:col-span-1 relative group overflow-hidden rounded-3xl"
            >
              <img
                // src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=90"
                src={Kilimanjaro2}
                alt="Mountain peak"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-mamba-sky-blue/80 to-transparent" />
            </motion.div>

            {/* Bottom left */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="col-span-2 lg:col-span-1 relative group overflow-hidden rounded-3xl"
            >
              <img
                // src="https://images.unsplash.com/photo-1519904981063-b0cf448d479e?w=600&q=90"
                src={Satima1}
                alt="Trail view"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-mamba-golden/80 to-transparent" />
            </motion.div>

            {/* Bottom right */}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="col-span-2 lg:col-span-1 relative group overflow-hidden rounded-3xl"
            >
              <img
                src={Longido1}
                // src="https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=600&q=90"
                alt="Camping scene"
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/80 to-transparent" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* NEW: Upcoming Events - Redesigned with new styling */}
      <section className="py-10 px-10 md:px-16 lg:px-48 bg-slate-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="w-full"
        >
          <motion.div
            variants={fadeInUp}
            className="flex justify-between items-end mb-12"
          >
            <div>
              <h2 className="text-4xl md:text-5xl font-montserrat font-bold text-mamba-navy mb-2">
                Upcoming Hikes
              </h2>
              <p className="text-lg text-slate-600 font-inter">
                Join us on our next expedition
              </p>
            </div>
            <button
              onClick={() => navigate("/events")}
              className="hidden md:flex items-center gap-2 text-mamba-sky-blue font-semibold hover:gap-4 transition-all"
            >
              View All
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {adventuresIsLoading ? (
              // Loading skeletons
              [...Array(4)].map((_, index) => (
                <div
                  key={`adventure-skeleton-${index}`}
                  className="bg-white rounded-2xl overflow-hidden shadow-lg border-2 border-mamba-sky-blue/20"
                >
                  <div className="relative h-64 bg-slate-200 animate-pulse" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-slate-200 rounded animate-pulse w-2/3" />
                    <div className="h-6 bg-slate-200 rounded animate-pulse w-full" />
                    <div className="h-4 bg-slate-200 rounded animate-pulse w-5/6" />
                  </div>
                </div>
              ))
            ) : adventures?.results && adventures.results.length > 0 ? (
              adventures.results.slice(0, 4).map((adventure, index) => (
                <motion.div
                  key={adventure.id}
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.15,
                    ease: "easeOut",
                  }}
                  whileHover={{ y: -8 }}
                  onClick={() => navigate(`/events/${adventure.slug}`)}
                  className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-mamba-sky-blue/20 hover:border-mamba-golden"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      src={
                        adventure.destination?.images?.[0]?.image_url ||
                        `https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=80`
                      }
                      alt={adventure.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = `https://images.unsplash.com/photo-1551632811-561732d1e306?w=600&q=80`;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />

                    {/* Difficulty badge */}
                    <div className="absolute top-4 right-4">
                      <span className={`px-3 py-1 backdrop-blur-sm text-xs font-bold rounded-full ${
                        {
                          'Beginner Friendly': 'bg-green-500/90 text-white',
                          'Moderate': 'bg-amber-500/90 text-white',
                          'Challenging': 'bg-orange-500/90 text-white',
                          'Very Hard': 'bg-red-500/90 text-white',
                          'Unspecified': 'bg-slate-500/90 text-white',
                        }[adventure.destination?.difficulty] || 'bg-slate-500/90 text-white'
                      }`}>
                        {adventure.destination?.difficulty || "Unspecified"}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-mamba-navy text-sm font-medium mb-2">
                      <MapPin className="w-4 h-4 text-mamba-sky-blue" />
                      {adventure.destination?.name || "Amazing Location"}
                    </div>
                    <h3 className="text-xl font-montserrat font-bold text-mamba-navy mb-3 group-hover:text-mamba-sky-blue transition-colors">
                      {adventure.name}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {/* {formatDate(adventure.start_date)} */}
                        {new Date(adventure.start_date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric"
                        })}
                      </div>
                      {/* <div className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {adventure.spots || "20"} spots
                      </div> */}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-2xl font-montserrat font-bold text-mamba-navy">
                        {adventure?.minimum_price?.currency || "KSh"}{" "}
                        {adventure?.minimum_price?.amount?.toLocaleString() || "3,000"}
                      </span>
                      <button className="text-mamba-sky-blue font-semibold group-hover:gap-2 flex items-center gap-1 transition-all">
                        Details
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              // No adventures available
              <div className="col-span-full text-center py-12">
                <p className="text-slate-600 text-lg font-inter">
                  No upcoming hikes available at the moment.
                </p>
              </div>
            )}
          </div>

          <div className="mt-8 text-center md:hidden">
            <button
              onClick={() => navigate("/events")}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white font-montserrat font-semibold rounded-full hover:shadow-xl transition-all"
            >
              View All Adventures
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </motion.div>
      </section>

      {/* Redesigned Shop Products Section */}
      <section className="relative py-24 px-10 md:px-16 lg:px-48 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {/* Section Header */}
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-12 bg-gradient-to-r from-transparent via-orange-500 to-transparent" />
              <FaInstagram className="text-3xl text-orange-500" />
              <div className="h-px w-12 bg-gradient-to-r from-transparent via-orange-500 to-transparent" />
            </div>
            <h2 className="text-3xl lg:text-5xl xl:text-6xl font-montserrat font-black text-mamba-navy leading-tight tracking-tight mb-4">
              Shop with
              <span className="block bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 bg-clip-text text-transparent">
                Mamba Hikers Gear
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto font-inter">
              Premium hiking equipment designed by adventurers, for adventurers
            </p>
          </motion.div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {shopProducts.map((product, index) => (
              <motion.div
                key={product.id}
                variants={fadeInUp}
                whileHover={{ y: -12 }}
                onClick={() => handleProductClick(product.instagramUrl)}
                className="group relative bg-gradient-to-br from-white to-slate-50 rounded-3xl overflow-hidden border-2 border-slate-100 hover:border-orange-200 transition-all duration-500 hover:shadow-2xl cursor-pointer"
              >
                {/* Featured Badge */}
                {product.featured && (
                  <div className="absolute top-4 left-4 z-10">
                    <div className="px-3 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold rounded-full shadow-lg">
                      FEATURED
                    </div>
                  </div>
                )}

                {/* Product Image/Icon */}
                <div className="relative h-64 bg-gradient-to-br from-mamba-navy via-slate-800 to-mamba-navy overflow-hidden">
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/80 via-mamba-navy/40 to-transparent" />

                  {/* Category Icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center transform group-hover:scale-110 transition-transform duration-500">
                      <div className="text-7xl mb-3 filter drop-shadow-2xl">
                        {product.category === "Headwear" && "🎩"}
                        {product.category === "Bags" && "🎒"}
                        {product.category === "Equipment" && "🥾"}
                        {product.category === "Hydration" && "💧"}
                      </div>
                      <div className="inline-block px-3 py-1 bg-white/10 backdrop-blur-md border border-white/20 rounded-full">
                        <span className="text-xs text-white font-semibold tracking-wider">
                          {product.category}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Instagram Icon Overlay */}
                  <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 shadow-lg group-hover:scale-110">
                    <FaInstagram className="text-white text-lg" />
                  </div>

                  {/* Accent Line */}
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
                </div>

                {/* Product Info */}
                <div className="p-6">
                  <h3 className="text-xl font-montserrat font-black text-mamba-navy mb-2 tracking-tight group-hover:text-orange-600 transition-colors duration-300">
                    {product.name}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4 leading-relaxed line-clamp-2 font-inter">
                    {product.description}
                  </p>

                  {/* CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      {product.category}
                    </span>
                    <div className="flex items-center gap-2 text-orange-600 font-semibold text-sm group-hover:gap-3 transition-all">
                      <span>View</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Instagram CTA */}
          <motion.div variants={fadeInUp} className="mt-16 text-center">
            <button
              onClick={() =>
                window.open(
                  "https://www.instagram.com/mambahikers_gear_shop?igsh=MThtc28xdGNuaWNqcw==",
                  "_blank",
                )
              }
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 overflow-hidden relative"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
              <FaInstagram className="w-5 h-5 relative" />
              <span className="relative">@mambahikers_gear_shop</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform relative" />
            </button>
          </motion.div>
        </motion.div>
      </section>

      {/* CREATIVE BANNER 3: Split Content with Image */}
      <section className="relative py-0 bg-white overflow-hidden">
        <div className="grid lg:grid-cols-2">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="flex items-center justify-center p-12 lg:p-20 bg-gradient-to-br from-mamba-bg-light to-mamba-sky-blue/10"
          >
            <div className="max-w-xl">
              <div className="inline-block mb-6 px-4 py-2 bg-mamba-golden/20 rounded-full">
                <span className="text-xs font-montserrat font-bold uppercase tracking-[0.2em] text-mamba-navy">
                  Equipment Ready
                </span>
              </div>
              <h2 className="text-4xl lg:text-5xl font-montserrat font-black text-mamba-navy mb-6 leading-tight">
                Gear Up for
                <span className="block bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent">
                  Your Adventure
                </span>
              </h2>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed font-inter">
                From premium trekking poles to hydration systems, we've got
                everything you need for a safe and comfortable journey.
              </p>
              <button
                onClick={() =>
                  window.open(
                    "https://www.instagram.com/mambahikers_gear_shop?igsh=MThtc28xdGNuaWNqcw==",
                    "_blank",
                  )
                }
                className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white font-montserrat font-bold rounded-full hover:shadow-2xl transition-all hover:scale-105"
              >
                <FaInstagram className="w-5 h-5" />
                Visit Our Gear Shop
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>

          {/* Right: Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative h-[400px] lg:h-auto"
          >
            <img
              src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=1200&q=90"
              alt="Hiking gear"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          </motion.div>
        </div>
      </section>

      {/* REDESIGNED Why Hike With Us Section - Minimalist & Creative */}
      <section className="relative py-24 lg:py-32 px-10 md:px-16 lg:px-48 overflow-hidden bg-gradient-to-b from-white to-slate-50">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-[0.02]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgb(0 0 0) 1px, transparent 0)`,
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="relative w-full"
        >
          {/* Section Header */}
          <motion.div variants={fadeInUp} className="text-center mb-20">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="h-px w-16 bg-gradient-to-r from-transparent to-mamba-golden" />
              <span className="text-xs font-montserrat font-bold uppercase tracking-[0.3em] text-mamba-navy">
                Why Choose Us
              </span>
              <div className="h-px w-16 bg-gradient-to-l from-transparent to-mamba-golden" />
            </div>
            <h2 className="text-4xl lg:text-6xl xl:text-7xl font-montserrat font-black text-mamba-navy mb-6 leading-[0.9] tracking-tight">
              Your Adventure,
              <span className="block bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent">
                Our Expertise
              </span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed font-inter">
              Experience the mountains with professional support, expert
              guidance, and a community that feels like family
            </p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Feature 1: Programs */}
            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              className="group relative"
            >
              <div className="relative h-full p-8 bg-white rounded-3xl border-2 border-mamba-sky-blue/20 hover:border-mamba-golden transition-all duration-500 hover:shadow-2xl">
                {/* Accent line */}
                <div className="absolute top-0 left-8 w-12 h-1 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue rounded-full" />

                {/* Icon */}
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-500">
                  <div className="inline-flex p-4 bg-gradient-to-br from-mamba-golden/10 to-mamba-sky-blue/10 rounded-2xl">
                    <BiWorld className="w-10 h-10 text-mamba-golden" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-montserrat font-bold text-mamba-navy mb-4 tracking-tight">
                  Progressive Programs
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  Meticulously structured journeys from beginner trails to
                  summit attempts. Every step designed for your growth.
                </p>
              </div>
            </motion.div>

            {/* Feature 2: Paramedic */}
            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              className="group relative"
            >
              <div className="relative h-full p-8 bg-white rounded-3xl border-2 border-mamba-sky-blue/20 hover:border-mamba-sky-blue transition-all duration-500 hover:shadow-2xl">
                {/* Accent line */}
                <div className="absolute top-0 left-8 w-12 h-1 bg-gradient-to-r from-mamba-sky-blue to-blue-400 rounded-full" />

                {/* Icon */}
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-500">
                  <div className="inline-flex p-4 bg-gradient-to-br from-mamba-sky-blue/10 to-blue-50 rounded-2xl">
                    <FaUserDoctor className="w-10 h-10 text-mamba-sky-blue" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-montserrat font-bold text-mamba-navy mb-4 tracking-tight">
                  Medical Support
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  Trained paramedics on every trail. Your well-being is our
                  priority with immediate professional care available.
                </p>
              </div>
            </motion.div>

            {/* Feature 3: Guides */}
            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              className="group relative"
            >
              <div className="relative h-full p-8 bg-white rounded-3xl border-2 border-mamba-sky-blue/20 hover:border-mamba-golden transition-all duration-500 hover:shadow-2xl">
                {/* Accent line */}
                <div className="absolute top-0 left-8 w-12 h-1 bg-gradient-to-r from-mamba-golden to-amber-500 rounded-full" />

                {/* Icon */}
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-500">
                  <div className="inline-flex p-4 bg-gradient-to-br from-mamba-golden/10 to-amber-50 rounded-2xl">
                    <FaHandsHelping className="w-10 h-10 text-mamba-golden" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-montserrat font-bold text-mamba-navy mb-4 tracking-tight">
                  Expert Guides
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  Experienced professionals with deep local knowledge. Safety,
                  insights, and passion in every expedition.
                </p>
              </div>
            </motion.div>

            {/* Feature 4: Community */}
            <motion.div
              variants={fadeInUp}
              whileHover={{ y: -8 }}
              className="group relative"
            >
              <div className="relative h-full p-8 bg-white rounded-3xl border-2 border-mamba-sky-blue/20 hover:border-mamba-navy transition-all duration-500 hover:shadow-2xl">
                {/* Accent line */}
                <div className="absolute top-0 left-8 w-12 h-1 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue rounded-full" />

                {/* Icon */}
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-500">
                  <div className="inline-flex p-4 bg-gradient-to-br from-mamba-navy/10 to-mamba-sky-blue/10 rounded-2xl">
                    <IoShieldCheckmarkOutline className="w-10 h-10 text-mamba-navy" />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-montserrat font-bold text-mamba-navy mb-4 tracking-tight">
                  Strong Community
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm">
                  A network of passionate hikers. Supportive environment where
                  every adventure becomes a shared journey.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Redesigned CTA Section */}
      <section className="relative min-h-[50vh] flex items-center overflow-hidden">
        {/* Parallax Background */}
        <div
          className="absolute inset-0 bg-call-to-action bg-cover bg-center"
          style={{
            transform: "translateZ(0)",
            backgroundAttachment: "fixed",
          }}
        >
          {/* Gradient Overlays - Mamba Theme */}
          <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/90 to-mamba-navy/90" />
          <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/70 via-transparent to-transparent" />
        </div>

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 5, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-40 -right-40 w-96 h-96 bg-mamba-sky-blue/10 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -5, 0],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-mamba-golden/10 rounded-full blur-3xl"
          />
        </div>

        {/* Content Container */}
        <div className="relative z-10 w-full px-10 md:px-16 lg:px-48 py-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-[50%] mx-auto"
          >
            {/* Main Content Card */}
            <div className="backdrop-blur-xl bg-white/5 rounded-3xl border border-white/10 p-12 md:p-16 shadow-2xl">
              {/* Decorative Top Border - Mamba Theme */}
              <div className="flex gap-2 mb-8">
                <div className="h-1 w-16 bg-gradient-to-r from-mamba-golden to-transparent rounded-full" />
                <div className="h-1 w-8 bg-gradient-to-r from-mamba-sky-blue to-transparent rounded-full" />
              </div>

              {/* Main Heading */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-5xl md:text-6xl lg:text-7xl font-montserrat font-black text-white mb-6 tracking-tight leading-tight"
              >
                Find Your{" "}
                <span className="bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-sky-blue bg-clip-text text-transparent">
                  Path
                </span>
                ,<br />
                Find Your{" "}
                <span className="bg-gradient-to-r from-mamba-sky-blue via-blue-400 to-mamba-golden bg-clip-text text-transparent">
                  People
                </span>
              </motion.h2>

              {/* Subheading */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-xl md:text-2xl text-slate-200 mb-10 max-w-2xl leading-relaxed font-inter"
              >
                Join a vibrant{" "}
                <span className="font-dancing italic text-mamba-golden">
                  Mamba Hikers
                </span>{" "}
                community. Experience breathtaking trails, forge lasting
                connections, and discover the mountains with those who share
                your passion.
              </motion.p>

              {/* CTA Button with Enhanced Design */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="flex flex-col sm:flex-row gap-4 items-start"
              >
                <motion.button
                  onClick={handleWhatsapp}
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="group relative px-10 py-5 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue text-white text-lg font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-mamba-golden/50 transition-all duration-300 overflow-hidden"
                >
                  {/* Button Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

                  <span className="relative flex items-center gap-3">
                    Join Our Community
                    <svg
                      className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2.5}
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </span>
                </motion.button>

                {/* Secondary Info */}
                <div className="flex items-center gap-6 text-slate-300 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-mamba-golden rounded-full animate-pulse" />
                    <span>2,500+ Active Members</span>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Bottom Stats Bar */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {[
                { label: "Weekly Hikes", value: "10+" },
                { label: "Expert Guides", value: "15+" },
                { label: "Destinations", value: "50+" },
                { label: "Years Experience", value: "8+" },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="backdrop-blur-md bg-white/5 rounded-2xl border-2 border-mamba-golden/30 p-6 text-center hover:bg-white/10 transition-all duration-300"
                >
                  <div className="text-3xl font-montserrat font-black bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent mb-1">
                    {stat.value}
                  </div>
                  <div className="text-slate-300 text-sm font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div> */}


          </motion.div>
        </div>
      </section>
    </ClientLayout>
  );
};

export default HomeHybrid;
